import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Link,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import { alpha } from '@mui/material/styles';

const experiences = [
  {
    type: 'work',
    title: 'Full-Stack Developer',
    subtitle: 'Node.js · TypeScript · React · Prisma ORM · PostgreSQL · Azure · Vercel',
    company: 'Xeemax Digitals',
    project: 'Enterprise Staffing CRM & ATS — SMS Group (US Client) · Live on Vercel + Railway',
    period: 'Dec 2024 – Present',
    isCurrent: true,
    description: [
      'Architected end-to-end multi-office Staffing CRM (Admin, Front Office, Back Office, Client Office) modeled after Avionté — sole developer, live on Vercel + Railway with PostgreSQL on Neon (Azure).',
      'Designed normalized PostgreSQL schema with Prisma ORM; built 25+ modular REST API endpoints with middleware-enforced RBAC across 3 user roles, optimized query performance, and consistent error handling.',
      'Implemented Promise.allSettled parallelism, server-side search (Prisma contains/insensitive), cursor pagination, and AbortController request cancellation — reducing unnecessary API calls by ~60%.',
      'Built RBAC authentication with JWT across multi-level user roles; secured all routes with middleware-level permission checks.',
      'Delivered full ATS recruitment lifecycle: job posting, applicant intake portal, interview scheduling, task tracking, document/contract management, and customizable hiring pipelines.',
      'Developed React.js frontend with MUI, role-driven dashboards (25 widget resolvers), debounced search, and a 5-tab user profile settings page with avatar & signature upload.',
      'Integrated Azure Blob Storage for document uploads; implemented three-state loading UX and client-side error boundaries.',
    ],
    link: null,
    color: '#8b5cf6',
  },
  {
    type: 'work',
    title: 'Backend Developer',
    subtitle: 'Django · Django REST Framework · PostgreSQL',
    company: 'NED University (CSIT Department)',
    project: 'Inventory Management System',
    period: 'Mar 2025 – May 2025',
    isCurrent: false,
    description: [
      'Led backend development using Django & Django REST Framework for a university-wide inventory system.',
      'Designed and implemented RESTful APIs for smooth frontend-backend integration.',
      'Improved performance via pagination and scalable MVC architecture.',
      'Implemented JWT authentication and role-based authorization.',
    ],
    link: 'https://ned-inventory-management-system.vercel.app/',
    color: '#6366f1',
  },
  {
    type: 'work',
    title: 'Intern',
    subtitle: 'Infrastructure & Development',
    company: 'Karachi Development Authority (KDA)',
    project: null,
    period: 'Mar 2025 – Apr 2025',
    isCurrent: false,
    description: [
      'Assisted in documentation and system workflow analysis for a large-scale government organization.',
      'Observed and supported enterprise infrastructure and development practices.',
    ],
    link: null,
    color: '#10b981',
  },
];

const education = [
  {
    type: 'education',
    degree: 'Bachelor of Science in Computer Science',
    institution: 'NED University of Engineering & Technology',
    period: '2022 – 2026',
    details: 'CGPA: 3.1 / 4.0 · One of Pakistan\'s top-ranked engineering universities. Coursework: Data Structures, Algorithms, Database Systems, AI, Software Engineering, Operating Systems.',
    color: '#06b6d4',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } },
};

function SectionLabel({ text, icon }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 2, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)' }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.15em', color: '#6366f1', textTransform: 'uppercase' }}>
        {text}
      </Typography>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(99,102,241,0.3) 0%, transparent 100%)' }} />
    </Box>
  );
}

function ExperienceCard({ exp }) {
  return (
    <motion.div variants={cardVariants}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'rgba(10,18,40,0.6)',
          backdropFilter: 'blur(12px)',
          border: `1px solid rgba(255,255,255,0.06)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            borderColor: alpha(exp.color, 0.3),
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 24px ${alpha(exp.color, 0.12)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${exp.color}, ${alpha(exp.color, 0.3)})`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc', fontSize: { xs: '1.1rem', md: '1.25rem' }, fontFamily: "'Space Grotesk', sans-serif" }}>
                  {exp.title}
                </Typography>
                {exp.isCurrent && (
                  <Chip label="Current" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', letterSpacing: '0.04em' }} />
                )}
              </Box>
              <Typography sx={{ color: exp.color, fontWeight: 600, mb: 0.5, fontSize: '0.9375rem' }}>{exp.subtitle}</Typography>
              <Typography sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.875rem' }}>
                {exp.company}{exp.project ? ` · ${exp.project}` : ''}
              </Typography>
            </Box>
            <Chip
              label={exp.period}
              size="small"
              sx={{ height: 28, fontWeight: 600, fontSize: '0.8rem', color: '#cbd5e1', background: alpha(exp.color, 0.08), border: `1px solid ${alpha(exp.color, 0.2)}` }}
            />
          </Box>

          {/* Bullets */}
          <Box>
            {exp.description.map((pt, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5, '&:last-child': { mb: 0 } }}>
                <CheckCircleOutlineIcon sx={{ color: exp.color, fontSize: '1rem', mt: 0.3, flexShrink: 0, opacity: 0.8 }} />
                <Typography sx={{ color: '#94a3b8', lineHeight: 1.75, fontSize: '0.9375rem' }}>{pt}</Typography>
              </Box>
            ))}
          </Box>

          {exp.link && (
            <Box sx={{ pt: 2.5, mt: 2.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Link href={exp.link} target="_blank" rel="noopener noreferrer" underline="none"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, fontSize: '0.875rem', color: exp.color, transition: 'gap 0.2s ease', '&:hover': { gap: 1.25 } }}
              >
                View Live Project <LaunchIcon sx={{ fontSize: '1rem' }} />
              </Link>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function EducationCard({ edu }) {
  return (
    <motion.div variants={cardVariants}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'rgba(10,18,40,0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.06)',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            borderColor: alpha(edu.color, 0.3),
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 24px ${alpha(edu.color, 0.1)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${edu.color}, ${alpha(edu.color, 0.3)})`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc', fontSize: { xs: '1.1rem', md: '1.2rem' }, mb: 0.5, fontFamily: "'Space Grotesk', sans-serif" }}>
                {edu.degree}
              </Typography>
              <Typography sx={{ color: edu.color, fontWeight: 600, fontSize: '0.9375rem' }}>{edu.institution}</Typography>
            </Box>
            <Chip
              label={edu.period}
              size="small"
              sx={{ height: 28, fontWeight: 600, fontSize: '0.8rem', color: '#cbd5e1', background: alpha(edu.color, 0.08), border: `1px solid ${alpha(edu.color, 0.2)}` }}
            />
          </Box>
          <Typography sx={{ color: '#94a3b8', lineHeight: 1.75, fontSize: '0.9375rem' }}>{edu.details}</Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function Experience() {
  return (
    <Container id="experience" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 700, letterSpacing: '0.18em', mb: 1, display: 'block', fontSize: '0.75rem' }}>
            BACKGROUND
          </Typography>
          <Typography component="h2" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: { xs: '2rem', md: '2.75rem' }, letterSpacing: '-0.02em', mb: 2, background: 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Experience &amp; Education
          </Typography>
          <Typography sx={{ color: '#64748b', maxWidth: 540, mx: 'auto', fontSize: '1.0625rem', lineHeight: 1.7 }}>
            Real-world contributions and academic foundation
          </Typography>
        </Box>

        {/* Work Experience */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }}>
          <SectionLabel text="Work Experience" icon={<WorkIcon sx={{ fontSize: '1rem', color: '#6366f1' }} />} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 6 }}>
            {experiences.map((exp, i) => (
              <ExperienceCard key={i} exp={exp} />
            ))}
          </Box>

          {/* Education */}
          <SectionLabel text="Education" icon={<SchoolIcon sx={{ fontSize: '1rem', color: '#6366f1' }} />} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {education.map((edu, i) => (
              <EducationCard key={i} edu={edu} />
            ))}
          </Box>
        </motion.div>
      </motion.div>
    </Container>
  );
}

export default Experience;
